import { Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAreasShort } from 'services/areas-service';
import { getProcessesShort } from 'services/processes-service';
import { Select, TextField } from 'Components/ui';
import styled from 'styled-components';

const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const LABEL_COLOR = 'var(--vo-ink-2)';

const FieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${LABEL_COLOR};
`;
const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
  margin: 0;
`;
const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  padding-top: 4px;
`;
const CancelBtn = styled.button`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: ${MUTED};
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  &:hover { background: var(--vo-mist); }
`;
const SubmitBtn = styled.button`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  &:hover { background: ${BLUE_DEEP}; }
`;

export const GenerateNewReportRopa = ({
  handleComplete,
  register,
  control,
  handleSubmit,
  errors,
  watch,
  selectedTenant,
}) => {
  const { t } = useTranslation(['mapping', 'dashboard', 'common']);
  const navigate = useNavigate();
  const scope = watch('scope');

  const SCOPE_OPTIONS = [
    { value: 'Empresa',           label: t('mapping:ropa.reportForm.scopes.company') },
    { value: 'Área',              label: t('mapping:ropa.reportForm.scopes.area') },
    { value: 'Processo',          label: t('mapping:ropa.reportForm.scopes.process') },
    { value: 'Sumário Executivo', label: t('mapping:ropa.reportForm.scopes.executiveSummary') },
  ];

  const { data: areas } = useQuery(['getAreas', scope], async () => {
    if (!selectedTenant.slug || scope === 'Empresa' || scope === 'Processo') return [];
    const result = await getAreasShort(selectedTenant.slug);
    return result?.data.length > 0 ? result.data : [];
  });

  const { data: processes } = useQuery(['getProcesses', scope], async () => {
    if (!selectedTenant.slug || scope === 'Empresa' || scope === 'Área') return [];
    const result = await getProcessesShort(selectedTenant.slug);
    return result?.data.length > 0 ? result.data : [];
  });

  const onSubmit = () => handleComplete();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldsWrap>
        {/* Name */}
        <FieldGroup data-tour="report-name">
          <Label htmlFor="ropa-name">{t('mapping:ropa.reportForm.reportName')} *</Label>
          <TextField
            id="ropa-name"
            type="text"
            fullWidth
            placeholder="Ex: ROPA Empresa — Junho 2025"
            error={!!errors?.name}
            {...register('name', { required: t('mapping:ropa.reportForm.nameRequired') })}
          />
          {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
        </FieldGroup>

        {/* Scope */}
        <FieldGroup data-tour="report-scope">
          <Label htmlFor="ropa-scope">{t('mapping:ropa.reportForm.scope')} *</Label>
          <Controller
            control={control}
            name="scope"
            rules={{
              validate: v => v !== '' || t('mapping:ropa.reportForm.scopeRequired'),
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                name="scope"
                value={value}
                onChange={onChange}
                error={!!errors?.scope}
                options={[
                  { value: '', label: t('mapping:ropa.reportForm.selectScope'), disabled: true },
                  ...SCOPE_OPTIONS,
                ]}
              />
            )}
          />
          {errors?.scope && <ErrorMsg>{errors.scope.message}</ErrorMsg>}
        </FieldGroup>

        {/* Area (conditional) */}
        {scope === 'Área' && areas && areas.length > 0 && (
          <FieldGroup>
            <Label htmlFor="ropa-area">{t('mapping:ropa.reportForm.area')} *</Label>
            <Controller
              control={control}
              name="areaId"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="areaId"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('mapping:ropa.reportForm.selectArea') },
                    ...areas.filter(a => !a.archived).map(a => ({ value: a._id, label: a.name })),
                  ]}
                />
              )}
            />
          </FieldGroup>
        )}

        {/* Process (conditional) */}
        {scope === 'Processo' && processes && processes.length > 0 && (
          <FieldGroup>
            <Label htmlFor="ropa-process">{t('mapping:ropa.reportForm.process')} *</Label>
            <Controller
              control={control}
              name="processId"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="processId"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('mapping:ropa.reportForm.selectProcess') },
                    ...processes.filter(p => !p.archived).map(p => ({ value: p._id, label: p.name })),
                  ]}
                />
              )}
            />
          </FieldGroup>
        )}

        {/* Buttons */}
        <BtnRow data-tour="form-actions">
          <CancelBtn type="button" onClick={() => navigate('/reports/ropa')}>
            {t('common:buttons.cancel')}
          </CancelBtn>
          <SubmitBtn type="submit">
            {t('dashboard:ropa.form.generateButton')}
          </SubmitBtn>
        </BtnRow>
      </FieldsWrap>
    </form>
  );
};
